import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useSummer2026 } from '../context/Summer2026Context';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Users, RefreshCw, ChevronRight, ChevronLeft,
  Loader2, AlertCircle, LogOut, FileText, Calendar, Waves, Search, X, ClipboardList, Settings, Bus, Trash2, CalendarOff, Eye, EyeOff
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '../components/ui/input';
import Summer2026GroupDetail from '../components/Summer2026GroupDetail';
import FrontDeskLogViewer from '../components/FrontDeskLogViewer';
import BusAttendanceViewer from '../components/BusAttendanceViewer';
import AttendanceReports from '../components/AttendanceReports';

const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && !window.location.hostname.includes('preview.emergentagent.com') ? `${window.location.origin}/api` : `${process.env.REACT_APP_BACKEND_URL}/api`;
const safeFetch = (...args) => (window.__origFetch || window.fetch)(...args);

const SESSION_COLORS = {
  full: { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-600' },
  half1: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-600' },
  half2: { bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-600' },
  '6week': { bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-600' },
  flex20: { bg: 'bg-pink-100', text: 'text-pink-800', dot: 'bg-pink-600' },
  flex25: { bg: 'bg-teal-100', text: 'text-teal-800', dot: 'bg-teal-600' },
  flex30: { bg: 'bg-indigo-100', text: 'text-indigo-800', dot: 'bg-indigo-600' },
};

const BUNK_COLORS = [
  '#1E40AF', '#15803D', '#7E22CE', '#C2410C',
  '#BE185D', '#0F766E', '#3730A3', '#B45309',
];

function formatDate(isoDate) {
  const d = new Date(isoDate + 'T12:00:00');
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return { short: `${month}/${day}`, day: days[d.getDay()], full: d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) };
}

export default function Summer2026Dashboard({ onSwitchMode }) {
  const {
    selectedDate, setSelectedDate, dates,
    getGroupsForDate, getCampersForDate,
    syncRoster, loading, error, lastSync,
    logout, isAdmin, loggedInGroup, bunks, roster,
    getAttendance, sessionTypes, loadAttendanceFromBackend,
    fetchBusAttendance, attendance, clearLocalAttendance,
  } = useSummer2026();

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFrontDeskLog, setShowFrontDeskLog] = useState(false);
  const [showBusAttendance, setShowBusAttendance] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [showSeasonMgmt, setShowSeasonMgmt] = useState(false);
  const [showPlannedAbsences, setShowPlannedAbsences] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [clearAttDate, setClearAttDate] = useState('all');
  const [clearingAtt, setClearingAtt] = useState(false);
  const [clearAttResult, setClearAttResult] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [resetDone, setResetDone] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [passcodeValue, setPasscodeValue] = useState('');
  const [passcodeVisible, setPasscodeVisible] = useState(false);
  const [passcodeSaving, setPasscodeSaving] = useState(false);
  const [passcodeMsg, setPasscodeMsg] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoProgress, setPhotoProgress] = useState(0);
  const [photoMsg, setPhotoMsg] = useState(null);
  const [photoStatus, setPhotoStatus] = useState(null);
  const photoFileRef = useRef(null);
  const [earlyLogTimes, setEarlyLogTimes] = useState(() => new Map());
  const dateScrollRef = useRef(null);

  // Fetch early dismissal front desk logs so stats include front-desk sign-outs & recurring dismissals
  useEffect(() => {
    if (!isAdmin || !selectedDate) return;
    (async () => {
      try {
        const res = await safeFetch(`${API}/summer2026/frontdesk/logs?date=${selectedDate}&logType=early_dismissal`);
        const data = await res.json();
        if (data.success) {
          const m = new Map();
          (data.logs || []).forEach(l => m.set(String(l.personId), l.time || ''));
          setEarlyLogTimes(m);
        }
      } catch { setEarlyLogTimes(new Map()); }
    })();
  }, [isAdmin, selectedDate, lastSync]);

  const effectiveStatus = (personId, att) =>
    earlyLogTimes.has(String(personId)) ? 'leaving' : att.status;

  // Load cancel-dismissal passcode + photo status when Season Management opens
  useEffect(() => {
    if (!showSeasonMgmt || !isAdmin) return;
    (async () => {
      try {
        const res = await safeFetch(`${API}/summer2026/settings/cancel-passcode`, {
          headers: { 'X-Admin-Password': 'Camp1993' },
        });
        const data = await res.json();
        if (data.success) setPasscodeValue(data.passcode || '');
      } catch {}
      try {
        const res = await safeFetch(`${API}/summer2026/camper-photos/status`);
        const data = await res.json();
        if (data.success) setPhotoStatus(data);
      } catch {}
    })();
  }, [showSeasonMgmt, isAdmin]);

  const uploadPhotoReport = async (file) => {
    setPhotoUploading(true);
    setPhotoProgress(0);
    setPhotoMsg(null);
    try {
      const uploadId = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      const chunkSize = 4 * 1024 * 1024;
      const totalChunks = Math.ceil(file.size / chunkSize);
      for (let i = 0; i < totalChunks; i++) {
        const fd = new FormData();
        fd.append('chunk', file.slice(i * chunkSize, (i + 1) * chunkSize));
        fd.append('uploadId', uploadId);
        fd.append('chunkIndex', i);
        fd.append('totalChunks', totalChunks);
        const r = await safeFetch(`${API}/summer2026/camper-photos/upload-chunk`, {
          method: 'POST',
          headers: { 'X-Admin-Password': 'Camp1993' },
          body: fd,
        });
        if (!r.ok) throw new Error(`Chunk ${i + 1}/${totalChunks} failed (HTTP ${r.status})`);
        setPhotoProgress(Math.round(((i + 1) / totalChunks) * 80));
      }
      const res = await safeFetch(`${API}/summer2026/camper-photos/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Password': 'Camp1993' },
        body: JSON.stringify({ uploadId }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Processing failed');
      setPhotoProgress(100);
      setPhotoMsg(`${data.photos_stored} camper photos uploaded!${data.links_failed ? ` (${data.links_failed} photo links failed to download)` : ''}`);
      try {
        const sres = await safeFetch(`${API}/summer2026/camper-photos/status`);
        const sdata = await sres.json();
        if (sdata.success) setPhotoStatus(sdata);
      } catch {}
    } catch (err) {
      setPhotoMsg(`Upload failed: ${err.message}`);
    } finally {
      setPhotoUploading(false);
      if (photoFileRef.current) photoFileRef.current.value = '';
    }
  };

  const savePasscode = async () => {
    setPasscodeSaving(true);
    setPasscodeMsg(null);
    try {
      const res = await safeFetch(`${API}/summer2026/settings/cancel-passcode`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Password': 'Camp1993' },
        body: JSON.stringify({ passcode: passcodeValue }),
      });
      const data = await res.json();
      setPasscodeMsg(res.ok && data.success ? 'Passcode saved!' : (data.detail || 'Save failed'));
    } catch {
      setPasscodeMsg('Save failed');
    } finally {
      setPasscodeSaving(false);
      setTimeout(() => setPasscodeMsg(null), 5000);
    }
  };

  const groups = useMemo(() => getGroupsForDate(selectedDate || ''), [getGroupsForDate, selectedDate]);
  const currentBunk = bunks.find(b => b.code === loggedInGroup);

  // For non-admin, filter to only their bunk
  const visibleGroups = useMemo(() => {
    if (isAdmin) return groups;
    const filtered = {};
    if (groups[loggedInGroup]) filtered[loggedInGroup] = groups[loggedInGroup];
    return filtered;
  }, [groups, isAdmin, loggedInGroup]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await syncRoster();
    setRefreshing(false);
  };

  // Shared clear function
  const executeClear = async (scope) => {
    setClearingAtt(true);
    setClearAttResult(null);
    setConfirmClear(false);
    const params = scope && scope !== 'all' ? `?date=${scope}` : '?confirm=WIPE_ALL_DATES';
    const url = `${API}/summer2026/attendance/clear${params}`;

    try {
      const res = await safeFetch(url, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        const details = data.details || {};
        setClearAttResult({
          total: data.deleted,
          attendance: details.attendance_records || 0,
          busSync: details.bus_sync_records || 0,
          busOverrides: details.bus_overrides || 0,
          frontdeskLogs: details.frontdesk_logs || 0,
          camperNotes: details.camper_notes || 0,
          scope: data.scope || (scope === 'all' ? 'all dates' : scope),
        });
        clearLocalAttendance(scope === 'all' ? null : scope);
        if (typeof loadAttendanceFromBackend === 'function') loadAttendanceFromBackend(selectedDate);
        if (typeof fetchBusAttendance === 'function') fetchBusAttendance(selectedDate);
        setTimeout(() => setClearAttResult(null), 12000);
      } else {
        setClearAttResult({ total: -1, error: data.error || 'Clear failed' });
      }
    } catch (err) {
      console.error('Clear error:', err);
      setClearAttResult({ total: -1, error: err.message });
    } finally {
      setClearingAtt(false);
    }
  };


  // Scroll to selected date
  useEffect(() => {
    if (dateScrollRef.current && selectedDate) {
      const el = dateScrollRef.current.querySelector(`[data-date="${selectedDate}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [selectedDate]);

  const dateIdx = dates.indexOf(selectedDate);
  const goPrev = () => {
    if (dateIdx > 0) {
      setSelectedDate(dates[dateIdx - 1]);
    } else if (selectedDate) {
      // Step back 1 calendar day when outside camp dates
      const d = new Date(selectedDate + 'T12:00:00');
      d.setDate(d.getDate() - 1);
      setSelectedDate(d.toISOString().split('T')[0]);
    }
  };
  const goNext = () => {
    if (dateIdx >= 0 && dateIdx < dates.length - 1) {
      setSelectedDate(dates[dateIdx + 1]);
    } else if (selectedDate) {
      const d = new Date(selectedDate + 'T12:00:00');
      d.setDate(d.getDate() + 1);
      setSelectedDate(d.toISOString().split('T')[0]);
    }
  };

  const dateInfo = selectedDate ? formatDate(selectedDate) : null;
  const totalCampers = Object.values(visibleGroups).reduce((s, g) => s + g.campers.length, 0);

  // Search: filter groups to campers matching query
  const q = searchQuery.trim().toLowerCase();
  const filteredGroups = useMemo(() => {
    if (!q) return visibleGroups;
    const result = {};
    Object.entries(visibleGroups).forEach(([code, group]) => {
      const matched = group.campers.filter(c =>
        `${c.firstName} ${c.lastName}`.toLowerCase().includes(q)
      );
      if (matched.length > 0) {
        result[code] = { ...group, campers: matched };
      }
    });
    return result;
  }, [visibleGroups, q]);

  const searchResultCount = q ? Object.values(filteredGroups).reduce((s, g) => s + g.campers.length, 0) : null;

  const logoUrl = 'https://customer-assets.emergentagent.com/job_camp-attendance-app/artifacts/lojn686w_Riverview_Athletics_Logo.png';

  if (selectedGroup) {
    const grp = visibleGroups[selectedGroup];
    if (grp) {
      return (
        <Summer2026GroupDetail
          groupCode={selectedGroup}
          groupName={grp.name}
          campers={grp.campers}
          date={selectedDate}
          onBack={() => setSelectedGroup(null)}
          isAdmin={isAdmin}
        />
      );
    }
    setSelectedGroup(null);
  }

  if (showFrontDeskLog) {
    return <FrontDeskLogViewer date={selectedDate} onClose={() => setShowFrontDeskLog(false)} />;
  }

  if (showBusAttendance) {
    return <BusAttendanceViewer date={selectedDate} onClose={() => setShowBusAttendance(false)} />;
  }

  if (showReports) {
    return <AttendanceReports onClose={() => setShowReports(false)} />;
  }

  if (showPlannedAbsences) {
    return <PlannedAbsencesView roster={roster} onClose={() => setShowPlannedAbsences(false)} />;
  }

  if (showSeasonMgmt) {
    const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && !window.location.hostname.includes('preview.emergentagent.com') ? `${window.location.origin}/api` : `${process.env.REACT_APP_BACKEND_URL}/api`;
    const safeFetch = (...args) => (window.__origFetch || window.fetch)(...args);
    const handleReset = async () => {
      if (!window.confirm('This will ARCHIVE all front desk logs and camper notes, and start a fresh season. CampMinder data will re-sync from the API. Are you sure?')) return;
      setResetting(true);
      try {
        const year = new Date().getFullYear().toString();
        const res = await safeFetch(`${API}/summer2026/seasons/reset`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ year }),
        });
        const data = await res.json();
        if (data.success) {
          setResetDone(data.archived);
        }
      } catch (err) {
        console.error('Season reset error:', err);
      } finally {
        setResetting(false);
      }
    };

    return (
      <div className="min-h-screen bg-[#F1F5F9]">
        <div className="bg-[#0055A4] py-4 px-4">
          <div className="max-w-lg mx-auto flex items-center">
            <button onClick={() => { setShowSeasonMgmt(false); setResetDone(null); }} className="text-white mr-3">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <h1 className="text-white text-xl font-bold font-['Fredoka']">Season Management</h1>
          </div>
        </div>
        <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
          <div className="bg-white rounded-2xl shadow border p-6">
            <h2 className="text-lg font-bold text-[#0F172A] font-['Fredoka'] mb-2">Current Season</h2>
            <p className="text-sm text-[#475569] mb-4">
              Summer {new Date().getFullYear()} — CampMinder roster syncs live from the API each day.
              Holiday Camp reads from its Google Spreadsheet.
            </p>
            <div className="bg-blue-50 rounded-xl p-4 text-sm text-blue-800">
              Each new season (2026, 2027, 2028...) starts fresh. Roster data always comes live from CampMinder or the spreadsheet. Front desk logs and notes are archived and cleared.
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow border p-6">
            <h2 className="text-lg font-bold text-[#0F172A] font-['Fredoka'] mb-2">Cancel Dismissal Passcode</h2>
            <p className="text-sm text-[#475569] mb-4">
              Required at the Front Desk to cancel an incoming early dismissal. Any text works — letters or numbers, case-sensitive.
            </p>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Input
                  type={passcodeVisible ? 'text' : 'password'}
                  value={passcodeValue}
                  onChange={e => setPasscodeValue(e.target.value)}
                  placeholder="Enter passcode"
                  className="h-11 rounded-xl pr-10"
                  data-testid="cancel-passcode-input"
                />
                <button
                  type="button"
                  onClick={() => setPasscodeVisible(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]"
                  data-testid="cancel-passcode-toggle"
                >
                  {passcodeVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Button
                onClick={savePasscode}
                disabled={passcodeSaving || !passcodeValue.trim()}
                className="bg-[#0055A4] hover:bg-[#004488] text-white rounded-xl h-11 px-5"
                data-testid="cancel-passcode-save"
              >
                {passcodeSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
            {passcodeMsg && (
              <p className={`mt-2 text-sm font-medium ${passcodeMsg.includes('saved') ? 'text-green-700' : 'text-red-600'}`} data-testid="cancel-passcode-msg">
                {passcodeMsg}
              </p>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow border p-6">
            <h2 className="text-lg font-bold text-[#0F172A] font-['Fredoka'] mb-2">Camper Photos</h2>
            <p className="text-sm text-[#475569] mb-3">
              Upload the photo report (.xlsx or .csv with person ID, last name, first name, and the picture — embedded photo or a photo link).
              Photos appear next to camper names in group attendance and the Front Desk list.
            </p>
            {photoStatus && photoStatus.count > 0 && (
              <p className="text-sm font-medium text-green-700 mb-3" data-testid="photo-status">
                {photoStatus.count} photos stored{photoStatus.last_updated ? ` · updated ${new Date(photoStatus.last_updated).toLocaleDateString()}` : ''}
              </p>
            )}
            <input
              type="file"
              accept=".xlsx,.csv"
              ref={photoFileRef}
              className="hidden"
              onChange={e => e.target.files[0] && uploadPhotoReport(e.target.files[0])}
              data-testid="photo-report-input"
            />
            <Button
              onClick={() => photoFileRef.current && photoFileRef.current.click()}
              disabled={photoUploading}
              className="w-full bg-[#0055A4] hover:bg-[#004488] text-white rounded-xl h-12"
              data-testid="photo-report-upload-btn"
            >
              {photoUploading ? `Uploading… ${photoProgress}%` : 'Upload Photo Report (.xlsx or .csv)'}
            </Button>
            {photoMsg && (
              <p className={`mt-2 text-sm font-medium ${photoMsg.includes('failed') ? 'text-red-600' : 'text-green-700'}`} data-testid="photo-upload-msg">
                {photoMsg}
              </p>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow border p-6">
            <h2 className="text-lg font-bold text-[#0F172A] font-['Fredoka'] mb-2">Start New Season</h2>
            <p className="text-sm text-[#475569] mb-4">
              Archives all current front desk logs and camper notes, then clears them for the new season.
              CampMinder roster data is always fetched fresh — no reset needed for attendance.
            </p>

            {resetDone ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800">
                <p className="font-semibold mb-1">Season reset complete!</p>
                <p>Archived {resetDone.logs} front desk log{resetDone.logs !== 1 ? 's' : ''} and {resetDone.notes} camper note{resetDone.notes !== 1 ? 's' : ''}.</p>
                <p className="mt-2 text-xs text-green-600">Old data has been preserved in archive collections.</p>
              </div>
            ) : (
              <Button
                onClick={handleReset}
                disabled={resetting}
                className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl h-12"
                data-testid="season-reset-btn"
              >
                {resetting ? 'Archiving & Resetting...' : 'Archive & Start Fresh Season'}
              </Button>
            )}
          </div>

          {/* Attendance Lock Time */}
          <div className="bg-white rounded-2xl shadow border p-6" data-testid="attendance-lock-section">
            <h2 className="text-lg font-bold text-[#0F172A] font-['Fredoka'] mb-2">Attendance Lock Time</h2>
            <p className="text-sm text-[#475569] mb-4">
              Counselors can mark attendance until this time (EST). After this time, attendance is locked.
            </p>
            <div className="flex items-center gap-3">
              <input
                type="time"
                defaultValue={localStorage.getItem('attendance_lock_time') || '10:00'}
                onChange={e => {
                  const val = e.target.value;
                  localStorage.setItem('attendance_lock_time', val);
                  // Save to backend so it persists across devices
                  safeFetch(`${API}/summer2026/settings`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ key: 'attendance_lock_time', value: val }),
                  }).catch(() => {});
                }}
                className="border rounded-xl px-4 py-2.5 text-lg font-bold text-[#0F172A] w-36"
                data-testid="attendance-lock-time-input"
              />
              <p className="text-sm text-[#475569]">EST</p>
            </div>
            <p className="text-xs text-[#94A3B8] mt-2">Default: 10:00 AM. Change to keep attendance open later.</p>
          </div>

          {/* Clear Attendance for Testing */}
          <div className="bg-white rounded-2xl shadow border p-6" data-testid="clear-attendance-section">
            <h2 className="text-lg font-bold text-[#0F172A] font-['Fredoka'] mb-2">Clear Attendance (Testing)</h2>
            <p className="text-sm text-[#475569] mb-3">
              Clears all attendance marks for a specific date or all dates. Use for testing purposes.
            </p>
            <div className="flex gap-2 mb-3">
              <select
                value={clearAttDate}
                onChange={e => setClearAttDate(e.target.value)}
                className="flex-1 border rounded-xl px-3 py-2 text-sm"
                data-testid="clear-attendance-date-select"
              >
                <option value="all">All Dates</option>
                <option value={selectedDate || ''}>{selectedDate || 'Current Date'}</option>
                {dates.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            {clearAttResult ? (
              <div className={`border rounded-xl p-3 text-sm mb-3 ${clearAttResult.total >= 0 ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`} data-testid="clear-attendance-result">
                {clearAttResult.total >= 0 ? (
                  <>
                    <p className="font-semibold">Cleared {clearAttResult.total} total records:</p>
                    <ul className="mt-1 text-xs space-y-0.5">
                      <li>{clearAttResult.attendance} attendance records</li>
                      <li>{clearAttResult.busSync} bus sync records</li>
                      <li>{clearAttResult.busOverrides} bus override records</li>
                      <li>{clearAttResult.frontdeskLogs} front desk logs (late arrivals + early dismissals)</li>
                      <li>{clearAttResult.camperNotes} camper notes</li>
                    </ul>
                  </>
                ) : (
                  <p className="font-semibold">Error: {clearAttResult.error}</p>
                )}
              </div>
            ) : null}
            <Button
              onClick={() => executeClear(clearAttDate)}
              disabled={clearingAtt}
              className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl h-12 font-bold text-base"
              data-testid="clear-attendance-btn"
            >
              {clearingAtt ? 'CLEARING...' : `CLEAR ALL DATA${clearAttDate !== 'all' ? ` for ${clearAttDate}` : ' (ALL DATES)'}`}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#E2E8F0] shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logoUrl} alt="RRDC" className="w-10 h-10 rounded-full" />
            <div>
              <h1 className="text-lg font-bold text-[#0F172A] font-['Fredoka']" data-testid="summer-app-title">
                Summer 2026
              </h1>
              <p className="text-xs text-[#475569]">
                {isAdmin ? 'Admin View' : currentBunk ? `${currentBunk.name} (${currentBunk.code})` : ''}
                {lastSync && <span className="ml-2 text-green-600">Synced {new Date(lastSync).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="https://campersnapshot.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 bg-[#0055A4] hover:bg-[#003F7D] text-white px-3 py-1.5 rounded-full text-xs font-bold transition-colors shadow-sm"
              data-testid="snapshot-link-btn"
            >
              Back to Camper Snapshot →
            </a>
            {isAdmin && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowBusAttendance(true)}
                  className="rounded-full"
                  data-testid="admin-bus-attendance-btn"
                  title="Bus Attendance"
                >
                  <Bus className="w-5 h-5 text-[#475569]" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowReports(true)}
                  className="rounded-full"
                  data-testid="admin-reports-btn"
                  title="Attendance Reports (PDF)"
                >
                  <FileText className="w-5 h-5 text-[#475569]" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowFrontDeskLog(true)}
                  className="rounded-full"
                  data-testid="admin-frontdesk-log-btn"
                  title="Late/Early Log"
                >
                  <ClipboardList className="w-5 h-5 text-[#475569]" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowPlannedAbsences(true)}
                  className="rounded-full"
                  data-testid="admin-planned-absences-btn"
                  title="Planned Absences"
                >
                  <CalendarOff className="w-5 h-5 text-[#475569]" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowSeasonMgmt(true)}
                  className="rounded-full"
                  data-testid="admin-season-mgmt-btn"
                  title="Season Management"
                >
                  <Settings className="w-5 h-5 text-[#475569]" />
                </Button>
                {confirmClear ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => executeClear('all')}
                    disabled={clearingAtt}
                    className="rounded-full bg-red-100 hover:bg-red-200"
                    data-testid="clear-attendance-toolbar-confirm"
                    title="TAP AGAIN TO CLEAR ALL"
                  >
                    <Trash2 className={`w-5 h-5 text-red-600 ${clearingAtt ? 'animate-spin' : 'animate-pulse'}`} />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => { setConfirmClear(true); setTimeout(() => setConfirmClear(false), 4000); }}
                    disabled={clearingAtt}
                    className="rounded-full"
                    data-testid="clear-attendance-toolbar-btn"
                    title="Clear All Data (All Dates)"
                  >
                    <Trash2 className={`w-5 h-5 text-[#475569] ${clearingAtt ? 'animate-pulse' : ''}`} />
                  </Button>
                )}
              </>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              disabled={refreshing || loading}
              className="rounded-full"
              data-testid="summer-refresh-button"
            >
              <RefreshCw className={`w-5 h-5 text-[#475569] ${refreshing || loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="ghost" size="icon" onClick={onSwitchMode} className="rounded-full" data-testid="switch-mode-button">
              <Calendar className="w-5 h-5 text-[#475569]" />
            </Button>
            <Button variant="ghost" size="icon" onClick={logout} className="rounded-full" data-testid="summer-logout-button">
              <LogOut className="w-5 h-5 text-[#475569]" />
            </Button>
          </div>
        </div>
      </div>

      {/* Clear Attendance Result Banner */}
      {clearAttResult !== null && (
        <div className="max-w-3xl mx-auto px-4 mt-2">
          <div className={`border rounded-xl p-3 text-sm ${clearAttResult.total >= 0 ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`} data-testid="clear-att-banner">
            {clearAttResult.total >= 0 ? (
              <>
                <p className="font-semibold text-center">Cleared {clearAttResult.total} total records{clearAttResult.scope ? ` for ${clearAttResult.scope}` : ''}</p>
                <p className="text-xs text-center mt-1">{clearAttResult.attendance} attendance + {clearAttResult.busSync} bus + {clearAttResult.busOverrides} bus overrides + {clearAttResult.frontdeskLogs} front desk + {clearAttResult.camperNotes} notes</p>
              </>
            ) : (
              <p className="font-semibold text-center">CLEAR FAILED: {clearAttResult.error}</p>
            )}
          </div>
        </div>
      )}

      {/* Two-tap confirm banner */}
      {confirmClear && (
        <div className="max-w-3xl mx-auto px-4 mt-2">
          <div className="bg-red-50 border-2 border-red-400 rounded-xl p-3 text-sm text-red-800 text-center animate-pulse" data-testid="clear-confirm-banner">
            <p className="font-bold">Tap the red trash icon again to CLEAR ALL attendance data</p>
          </div>
        </div>
      )}

      {/* Date Navigation */}
      <div className="sticky top-[64px] z-40 bg-white/90 backdrop-blur-xl border-b border-[#E2E8F0]">
        <div className="max-w-3xl mx-auto px-2">
          {/* Date info bar */}
          <div className="flex items-center justify-between px-2 py-2">
            <Button variant="ghost" size="icon" onClick={goPrev} className="rounded-full h-8 w-8" data-testid="date-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="text-center relative">
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="hover:bg-gray-100 rounded-lg px-3 py-1 transition-colors"
                data-testid="date-picker-toggle"
              >
                <p className="text-sm font-bold text-[#0F172A] font-['Fredoka']" data-testid="selected-date-label">
                  {dateInfo?.full}
                </p>
                <p className="text-xs text-[#475569]">
                  {totalCampers} campers today
                  {dateIdx === -1 && selectedDate && <span className="ml-1 text-amber-600">(custom date)</span>}
                </p>
              </button>
              {showDatePicker && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-white rounded-xl shadow-lg border p-3 z-50" data-testid="date-picker-dropdown">
                  <input
                    type="date"
                    value={selectedDate || ''}
                    onChange={e => {
                      if (e.target.value) {
                        setSelectedDate(e.target.value);
                        setShowDatePicker(false);
                      }
                    }}
                    className="border rounded-lg px-3 py-2 text-sm font-medium"
                    data-testid="date-picker-input"
                  />
                </div>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={goNext} className="rounded-full h-8 w-8" data-testid="date-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Scrollable date pills */}
          <div
            ref={dateScrollRef}
            className="flex gap-1 overflow-x-auto pb-2 px-1 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {dates.map((d) => {
              const f = formatDate(d);
              const isActive = d === selectedDate;
              return (
                <button
                  key={d}
                  data-date={d}
                  data-testid={`summer-date-${d}`}
                  onClick={() => setSelectedDate(d)}
                  className={`flex-shrink-0 flex flex-col items-center px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    isActive
                      ? 'bg-[#0055A4] text-white shadow-md'
                      : 'bg-gray-100 text-[#475569] hover:bg-gray-200'
                  }`}
                >
                  <span className="text-[10px]">{f.day}</span>
                  <span className="font-bold">{f.short}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-4">
        {/* Real-time attendance stats */}
        {isAdmin && !loading && roster.length > 0 && (() => {
          const totals = { present: 0, absent: 0, late: 0, leaving: 0, not_selected: 0 };
          roster.forEach(c => {
            const att = getAttendance(c.personId, selectedDate);
            const st = effectiveStatus(c.personId, att);
            totals[st] = (totals[st] || 0) + 1;
          });
          const total = roster.length;
          const marked = total - totals.not_selected;
          return (
            <div className="mb-4" data-testid="admin-stats-dashboard">
              <div className="grid grid-cols-5 gap-2 mb-2">
                <button
                  onClick={() => setStatusFilter(statusFilter === 'present' ? null : 'present')}
                  className={`bg-green-50 border rounded-xl p-3 text-center transition-all ${statusFilter === 'present' ? 'border-green-500 ring-2 ring-green-300' : 'border-green-200 hover:border-green-400'}`}
                  data-testid="stat-present"
                >
                  <p className="text-2xl font-bold text-green-700">{totals.present}</p>
                  <p className="text-[10px] font-semibold text-green-600 uppercase">Present</p>
                </button>
                <button
                  onClick={() => setStatusFilter(statusFilter === 'absent' ? null : 'absent')}
                  className={`bg-red-50 border rounded-xl p-3 text-center transition-all ${statusFilter === 'absent' ? 'border-red-500 ring-2 ring-red-300' : 'border-red-200 hover:border-red-400'}`}
                  data-testid="stat-absent"
                >
                  <p className="text-2xl font-bold text-red-700">{totals.absent}</p>
                  <p className="text-[10px] font-semibold text-red-600 uppercase">Absent</p>
                </button>
                <button
                  onClick={() => setStatusFilter(statusFilter === 'late' ? null : 'late')}
                  className={`bg-amber-50 border rounded-xl p-3 text-center transition-all ${statusFilter === 'late' ? 'border-amber-500 ring-2 ring-amber-300' : 'border-amber-200 hover:border-amber-400'}`}
                  data-testid="stat-late"
                >
                  <p className="text-2xl font-bold text-amber-700">{totals.late}</p>
                  <p className="text-[10px] font-semibold text-amber-600 uppercase">Late</p>
                </button>
                <button
                  onClick={() => setStatusFilter(statusFilter === 'leaving' ? null : 'leaving')}
                  className={`bg-teal-50 border rounded-xl p-3 text-center transition-all ${statusFilter === 'leaving' ? 'border-teal-500 ring-2 ring-teal-300' : 'border-teal-200 hover:border-teal-400'}`}
                  data-testid="stat-leaving"
                >
                  <p className="text-2xl font-bold text-teal-700">{totals.leaving}</p>
                  <p className="text-[10px] font-semibold text-teal-600 uppercase">Left Early</p>
                </button>
                <button
                  onClick={() => setStatusFilter(statusFilter === 'not_selected' ? null : 'not_selected')}
                  className={`bg-slate-50 border rounded-xl p-3 text-center transition-all ${statusFilter === 'not_selected' ? 'border-slate-500 ring-2 ring-slate-300' : 'border-slate-200 hover:border-slate-400'}`}
                  data-testid="stat-not-marked"
                >
                  <p className="text-2xl font-bold text-slate-700">{totals.not_selected}</p>
                  <p className="text-[10px] font-semibold text-slate-600 uppercase">Not Marked</p>
                </button>
              </div>
              <div className="bg-white rounded-xl border border-[#E2E8F0] p-2.5 flex items-center justify-between">
                <p className="text-xs text-[#475569]">
                  <span className="font-bold text-[#0F172A]">{marked}</span> of <span className="font-bold">{total}</span> marked
                </p>
                <div className="flex items-center gap-1.5">
                  <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#0055A4] rounded-full transition-all" style={{ width: `${total ? (marked / total * 100) : 0}%` }} />
                  </div>
                  <span className="text-xs font-bold text-[#0055A4]">{total ? Math.round(marked / total * 100) : 0}%</span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Filtered camper list by status */}
        {statusFilter && isAdmin && !loading && roster.length > 0 && (() => {
          const statusLabels = { present: 'Present', absent: 'Absent', late: 'Late', leaving: 'Left Early', not_selected: 'Not Marked' };
          const statusColors = {
            present: { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-800', badge: 'bg-green-100 text-green-800' },
            absent: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-800', badge: 'bg-red-100 text-red-800' },
            late: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-800', badge: 'bg-amber-100 text-amber-800' },
            leaving: { bg: 'bg-teal-50', border: 'border-teal-300', text: 'text-teal-800', badge: 'bg-teal-100 text-teal-800' },
            not_selected: { bg: 'bg-slate-50', border: 'border-slate-300', text: 'text-slate-800', badge: 'bg-slate-200 text-slate-800' },
          };
          const colors = statusColors[statusFilter] || statusColors.present;
          const filteredByStatus = roster.filter(c => {
            const att = getAttendance(c.personId, selectedDate);
            return effectiveStatus(c.personId, att) === statusFilter;
          }).sort((a, b) => {
            const last = (a.lastName || '').localeCompare(b.lastName || '');
            return last !== 0 ? last : (a.firstName || '').localeCompare(b.firstName || '');
          });

          return (
            <div className="mb-4" data-testid="status-filter-panel">
              <div className={`${colors.bg} border ${colors.border} rounded-2xl p-4`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <h3 className={`text-lg font-bold ${colors.text} font-['Fredoka']`}>{statusLabels[statusFilter]}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${colors.badge}`}>{filteredByStatus.length}</span>
                  </div>
                  <button
                    onClick={() => setStatusFilter(null)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-white rounded-lg border text-sm font-medium text-[#475569] hover:bg-gray-50 transition-colors"
                    data-testid="status-filter-clear"
                  >
                    <X className="w-3.5 h-3.5" /> Clear
                  </button>
                </div>
                {filteredByStatus.length === 0 ? (
                  <p className="text-sm text-[#94A3B8] text-center py-4">No campers with this status</p>
                ) : (
                  <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
                    {filteredByStatus.map(c => {
                      const att = getAttendance(c.personId, selectedDate);
                      const si = SESSION_COLORS[c.sessionType] || SESSION_COLORS.full;
                      return (
                        <div key={c.personId} className="flex items-center justify-between bg-white rounded-xl px-3 py-2.5 border border-white/50" data-testid={`status-camper-${c.personId}`}>
                          <div className="min-w-0">
                            <p className="font-semibold text-sm text-[#0F172A]">{c.lastName}, {c.firstName}</p>
                            <p className="text-[11px] text-[#475569]">{c.bunkCode} — {c.bunkName}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {att.lateTime && <span className="text-[10px] font-medium text-amber-700">{att.lateTime}</span>}
                            {(att.earlyTime || earlyLogTimes.get(String(c.personId))) && <span className="text-[10px] font-medium text-teal-700">{att.earlyTime || earlyLogTimes.get(String(c.personId))}</span>}
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${si.bg} ${si.text}`}>
                              {c.sessionType === 'full' ? 'Full' : c.sessionType === 'half1' ? 'Half 1' : c.sessionType === 'half2' ? 'Half 2' : c.sessionType}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <Input
            data-testid="camper-search-input"
            placeholder="Search camper by name..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9 h-10 rounded-xl border-[#E2E8F0] bg-white text-sm"
          />
          {q && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]"
              data-testid="camper-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {searchResultCount !== null && (
          <p className="text-xs text-[#475569] mb-2 px-1" data-testid="search-result-count">
            {searchResultCount} camper{searchResultCount !== 1 ? 's' : ''} found for "{searchQuery.trim()}"
          </p>
        )}
        {/* Error */}
        {error && (
          <motion.div
            className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-start gap-3"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-700 font-medium">Error</p>
              <p className="text-red-600 text-sm mt-1">{error}</p>
              <Button size="sm" onClick={handleRefresh} className="mt-2 bg-red-600 hover:bg-red-700 text-white rounded-lg" data-testid="summer-retry-button">
                Retry
              </Button>
            </div>
          </motion.div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#0055A4] animate-spin" />
            <span className="ml-3 text-[#475569]">Syncing roster...</span>
          </div>
        )}

        {/* Group Cards */}
        {!loading && (
          <AnimatePresence mode="wait">
            <motion.div key="groups" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {Object.keys(filteredGroups).length === 0 ? (
                <div className="bg-white rounded-2xl shadow-lg border border-[#E2E8F0] p-8 text-center">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-[#0F172A] font-['Fredoka']">{q ? 'No Results' : 'No Campers Today'}</h3>
                  <p className="text-[#475569] mt-2">{q ? `No campers match "${searchQuery.trim()}"` : 'No campers are enrolled for this date.'}</p>
                  {!q && (
                    <Button
                      onClick={() => syncRoster(selectedDate)}
                      className="mt-4 bg-[#0055A4] hover:bg-[#003F7D] text-white rounded-xl"
                      data-testid="retry-roster-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" /> Retry Loading Roster
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(filteredGroups).map(([code, group], idx) => (
                    <GroupCard
                      key={code}
                      group={group}
                      date={selectedDate}
                      index={idx}
                      onClick={() => setSelectedGroup(code)}
                      color={BUNK_COLORS[bunks.findIndex(b => b.code === code) % BUNK_COLORS.length]}
                    />
                  ))}

                  {/* Session type legend */}
                  {isAdmin && (
                    <motion.div
                      className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-4 mt-4"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <h4 className="text-xs font-bold text-[#475569] uppercase tracking-wider mb-2">Session Types</h4>
                      <div className="flex flex-wrap gap-2">
                        {sessionTypes.map(st => {
                          const c = SESSION_COLORS[st.id] || SESSION_COLORS.full;
                          return (
                            <span key={st.id} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
                              <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                              {st.label}
                            </span>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-xl border-t border-[#E2E8F0] py-2 px-4 z-30">
        <div className="max-w-3xl mx-auto flex items-center justify-center">
        </div>
      </div>
    </div>
  );
}

function GroupCard({ group, date, index, onClick, color }) {
  const { getAttendance, getSwimLesson, getBusStatus } = useSummer2026();

  const stats = group.campers.reduce((acc, c) => {
    const att = getAttendance(c.personId, date);
    acc[att.status] = (acc[att.status] || 0) + 1;
    return acc;
  }, {});

  // Session type breakdown
  const sessionBreakdown = {};
  let swimCount = 0;
  let busAbsentCount = 0;
  group.campers.forEach(c => {
    sessionBreakdown[c.sessionType] = (sessionBreakdown[c.sessionType] || 0) + 1;
    if (getSwimLesson(c.personId, date)) swimCount++;
    const bus = getBusStatus(c.personId);
    if (bus && bus.status === 'absent') busAbsentCount++;
  });

  return (
    <motion.div
      className="bg-white rounded-2xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-[#E2E8F0] hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.1)] transition-all cursor-pointer overflow-hidden"
      onClick={onClick}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      data-testid={`summer-group-card-${group.code}`}
    >
      <div className="flex items-center p-5">
        <div className="w-1.5 h-16 rounded-full mr-4" style={{ backgroundColor: color }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-[#0F172A] font-['Fredoka']">{group.name}</h3>
            <span className="text-xs font-mono text-[#94A3B8]">({group.code})</span>
          </div>
          <p className="text-xs text-[#475569] mt-0.5">{group.division} Division</p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
              {group.campers.length} camper{group.campers.length !== 1 ? 's' : ''}
            </Badge>
            {stats.present > 0 && <span className="text-xs text-green-600">{stats.present} present</span>}
            {stats.late > 0 && <span className="text-xs text-amber-600">{stats.late} late</span>}
            {stats.leaving > 0 && <span className="text-xs text-teal-600">{stats.leaving} leaving</span>}
            {stats.absent > 0 && <span className="text-xs text-gray-500">{stats.absent} absent</span>}
            {stats.not_selected > 0 && <span className="text-xs text-yellow-600 font-semibold">{stats.not_selected} not selected</span>}
          </div>
          {/* Session type pills */}
          <div className="flex flex-wrap gap-1 mt-2">
            {swimCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-cyan-100 text-cyan-800 border border-cyan-200">
                <Waves className="w-3 h-3" />{swimCount} swim
              </span>
            )}
            {busAbsentCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-800 border border-red-200">
                <Bus className="w-3 h-3" />{busAbsentCount} absent bus
              </span>
            )}
            {Object.entries(sessionBreakdown).map(([type, count]) => {
              const c = SESSION_COLORS[type] || SESSION_COLORS.full;
              return (
                <span key={type} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${c.bg} ${c.text}`}>
                  {count}x {type}
                </span>
              );
            })}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-[#94A3B8] flex-shrink-0" />
      </div>
    </motion.div>
  );
}


function PlannedAbsencesView({ roster, onClose }) {
  const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && !window.location.hostname.includes('preview.emergentagent.com') ? `${window.location.origin}/api` : `${process.env.REACT_APP_BACKEND_URL}/api`;
  const safeFetch = (...args) => (window.__origFetch || window.fetch)(...args);

  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCamper, setSelectedCamper] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const fetchAbsences = async () => {
    try {
      const res = await safeFetch(`${API}/summer2026/planned-absences`);
      const data = await res.json();
      if (data.success) setAbsences(data.absences);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAbsences(); }, []);

  const q = search.trim().toLowerCase();
  const filtered = q ? roster.filter(c => `${c.firstName} ${c.lastName}`.toLowerCase().includes(q)) : [];

  const handleSave = async () => {
    if (!selectedCamper || !startDate || !endDate) return;
    setSaving(true);
    setResult(null);
    try {
      const res = await safeFetch(`${API}/summer2026/planned-absences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          person_id: String(selectedCamper.personId),
          first_name: selectedCamper.firstName,
          last_name: selectedCamper.lastName,
          group_code: selectedCamper.bunkCode,
          group_name: selectedCamper.bunkName,
          start_date: startDate,
          end_date: endDate,
          reason: reason.trim(),
        }),
      });
      const data = await res.json();
      if (data.success) {
        setResult({ ok: true, msg: `Marked absent for ${data.days_marked} camp day${data.days_marked !== 1 ? 's' : ''}` });
        setSelectedCamper(null);
        setSearch('');
        setStartDate('');
        setEndDate('');
        setReason('');
        fetchAbsences();
      } else {
        setResult({ ok: false, msg: data.error || 'Failed' });
      }
    } catch (e) {
      setResult({ ok: false, msg: e.message });
    } finally { setSaving(false); }
  };

  const handleDelete = async (a) => {
    try {
      await safeFetch(`${API}/summer2026/planned-absences?person_id=${a.person_id}&start_date=${a.start_date}&end_date=${a.end_date}`, { method: 'DELETE' });
      fetchAbsences();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <div className="bg-[#0055A4] py-4 px-4">
        <div className="max-w-lg mx-auto flex items-center">
          <button onClick={onClose} className="text-white mr-3"><ChevronLeft className="w-6 h-6" /></button>
          <h1 className="text-white text-xl font-bold font-['Fredoka']">Planned Absences</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        {/* Add new */}
        <div className="bg-white rounded-2xl shadow border p-5">
          <h2 className="text-base font-bold text-[#0F172A] font-['Fredoka'] mb-3">Add Planned Absence</h2>

          {/* Camper search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <Input
              placeholder="Search camper..."
              value={selectedCamper ? `${selectedCamper.firstName} ${selectedCamper.lastName} (${selectedCamper.bunkCode})` : search}
              onChange={e => { setSearch(e.target.value); setSelectedCamper(null); }}
              className="pl-9 h-10 rounded-xl text-sm"
              data-testid="planned-absence-search"
            />
            {selectedCamper && (
              <button onClick={() => { setSelectedCamper(null); setSearch(''); }} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X className="w-4 h-4 text-[#94A3B8]" />
              </button>
            )}
          </div>

          {/* Search results */}
          {q && !selectedCamper && filtered.length > 0 && (
            <div className="bg-gray-50 rounded-xl border mb-3 max-h-48 overflow-y-auto">
              {filtered.slice(0, 20).map(c => (
                <button
                  key={c.personId}
                  onClick={() => { setSelectedCamper(c); setSearch(''); }}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b last:border-0"
                >
                  <span className="font-semibold">{c.firstName} {c.lastName}</span>
                  <span className="text-[#94A3B8] ml-2">{c.bunkCode} — {c.bunkName}</span>
                </button>
              ))}
            </div>
          )}

          {selectedCamper && (
            <>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-xs font-medium text-[#475569] mb-1 block">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    className="w-full border rounded-xl px-3 py-2 text-sm"
                    data-testid="planned-absence-start"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#475569] mb-1 block">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    className="w-full border rounded-xl px-3 py-2 text-sm"
                    data-testid="planned-absence-end"
                  />
                </div>
              </div>
              <Input
                placeholder="Reason (optional)"
                value={reason}
                onChange={e => setReason(e.target.value)}
                className="h-10 rounded-xl text-sm mb-3"
                data-testid="planned-absence-reason"
              />
              <Button
                onClick={handleSave}
                disabled={saving || !startDate || !endDate}
                className="w-full bg-[#0055A4] hover:bg-[#003F7D] text-white rounded-xl h-10"
                data-testid="planned-absence-save"
              >
                {saving ? 'Saving...' : 'Mark Absent'}
              </Button>
            </>
          )}

          {result && (
            <div className={`mt-3 rounded-xl p-3 text-sm ${result.ok ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
              {result.msg}
            </div>
          )}
        </div>

        {/* Existing planned absences */}
        <div className="bg-white rounded-2xl shadow border p-5">
          <h2 className="text-base font-bold text-[#0F172A] font-['Fredoka'] mb-3">
            Active Planned Absences {!loading && `(${absences.length})`}
          </h2>
          {loading ? (
            <p className="text-sm text-[#94A3B8] text-center py-4">Loading...</p>
          ) : absences.length === 0 ? (
            <p className="text-sm text-[#94A3B8] text-center py-4">No planned absences</p>
          ) : (
            <div className="space-y-2">
              {absences.map((a, i) => (
                <div key={i} className="flex items-center justify-between bg-gray-50 rounded-xl p-3 border">
                  <div>
                    <p className="font-semibold text-sm text-[#0F172A]">{a.first_name} {a.last_name}</p>
                    <p className="text-xs text-[#475569]">{a.group_code} — {a.start_date} to {a.end_date}</p>
                    {a.reason && <p className="text-xs text-[#94A3B8] italic mt-0.5">{a.reason}</p>}
                    <p className="text-[10px] text-[#94A3B8] mt-0.5">{a.camp_days?.length || 0} camp days</p>
                  </div>
                  <button
                    onClick={() => handleDelete(a)}
                    className="text-red-500 hover:text-red-700 p-1"
                    title="Remove planned absence"
                    data-testid={`delete-absence-${i}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
